/**
 * ListToolbar
 * ───────────────────────────────────────────────────────────────────────────
 * A drop-in replacement for the ad-hoc [search + filter + sort + location +
 * add-button] rows that appear on every list screen (Staff, Vendor, Product,
 * Orders, Bills, Purchase-Receive …).
 *
 * Usage
 * ─────
 *   <ListToolbar
 *     search={{ value: searchText, onChange: setSearchText, placeholder: 'Search name, email' }}
 *     filter={{ count: filterCount, onPress: () => setFilterOpen(true) }}
 *     sort={{ onPress: () => setSortVisible(true) }}
 *     location={{ label: selectedLocation?.locationName ?? 'All Location', onPress: openLocationPicker }}
 *   />
 *
 * All props except `search` are optional — omit a config object to hide that
 * control entirely.
 *
 * On portrait screens the "Add" button is hidden from the toolbar (the parent
 * is expected to render a FAB). Pass `forceAddButton` to always show it.
 */

import React, { FC, useState } from 'react';
import { Keyboard, TouchableOpacity, useWindowDimensions, View } from 'react-native';
import { ThemeConsumer } from '@rneui/themed';
import { ZenIcon, ZenSearchBar, ZenText } from 'zen-core-elements';

import { listToolbarStyle } from './listToolbar.style';
import { ListToolbarProps } from './listToolbar.types';
import { useKeyborad } from '../../hooks/useKeyborad';
import LocationPickerModal from './locationPickerModal';
import CommonSortModal from '../commonSortModal';

const ListToolbar: FC<ListToolbarProps> = ({
    search,
    filter,
    sort,
    location,
    buttons,
    forceAddButton = false,
    containerStyle,

}) => {
    const { width, height } = useWindowDimensions();
    const isLandscape = width > height;
    const isKeyboardOpen = useKeyborad();
    const showButtonsInToolbar = Boolean(buttons?.length && (isLandscape || forceAddButton));

    const [locationModalOpen, setLocationModalOpen] = useState(false);
    //State variable to store boolean value for sort popover
    const [isVisibleSort, setIsVisibleSort] = useState<boolean>(false)

    return (
        <>
            <ThemeConsumer>
                {({ theme }) => {
                    const styles = listToolbarStyle(theme);

                    return (
                        <View
                            onStartShouldSetResponder={() => {
                                Keyboard.dismiss();
                                return false;
                            }}
                            style={[styles.container, containerStyle]}
                        >
                            {/* ── Search bar ─────────────────────────────────── */}
                            {search && <View style={styles.searchWrapper}>
                                <ZenSearchBar
                                    search={search.value ?? ''}
                                    setSearch={(val: React.SetStateAction<string>) => {
                                        if (typeof val === 'function') {
                                            search.onChange((prev: string) => val(prev ?? ''));
                                        } else {
                                            search.onChange(val);
                                        }
                                    }}
                                    onCancel={() => {
                                        if (!isKeyboardOpen) {
                                            Keyboard.dismiss();
                                        }
                                        search.onCancel?.();
                                        search.onChange('');
                                    }}
                                    placeholder={search.placeholder ?? 'Search…'}
                                    placeholderTextColor={theme?.lightDark}
                                />
                            </View>}

                            {/* ── Filter icon button ─────────────────────────── */}
                            {filter && (
                                <TouchableOpacity
                                    style={styles.iconButton}
                                    onPress={() => {
                                        Keyboard.dismiss();
                                        filter.onPress();
                                    }}
                                    accessibilityLabel="Filter"
                                >
                                    <ZenIcon name="tune-variant" type="material-community" size={21} color={theme?.primary} />
                                    {/* Badge */}
                                    {!!filter.count && filter.count > 0 && (
                                        <View style={styles.filterBadge}>
                                            <ZenText content={filter.count > 9 ? '9+' : filter.count} bold color={theme?.light} size={theme?.badgeFontSize} />
                                        </View>
                                    )}
                                </TouchableOpacity>
                            )}

                            {/* ── Sort icon button ───────────────────────────── */}
                            {sort && (
                                <TouchableOpacity
                                    style={[styles.iconButton, styles.iconButtonActive]}
                                    onPress={() => {
                                        Keyboard.dismiss();
                                        setIsVisibleSort(true)
                                    }}
                                    accessibilityLabel="Sort"
                                >
                                    <ZenIcon
                                        type="material"
                                        name="swap-vert"
                                        color={theme?.primary}
                                        size={21}
                                    />

                                </TouchableOpacity>
                            )}

                            {/* ── Right cluster: Location + Add + Extra Buttons ── */}
                            {(location || showButtonsInToolbar) && (
                                <View style={styles.rightCluster}>

                                    {/* Location outline button */}
                                    {location && (
                                        <TouchableOpacity
                                            activeOpacity={0.7}
                                            style={{
                                                backgroundColor: theme?.coolGray,
                                                paddingVertical: 10, paddingHorizontal: 12,
                                                borderRadius: 8
                                            }}
                                            onPress={() => {
                                                Keyboard.dismiss();
                                                location.onOpen?.();
                                                setLocationModalOpen(true);
                                            }}
                                            accessibilityLabel="Select location"
                                        >
                                            <ZenText
                                                content={`📍 ${location.label || "All Location"}`}
                                                size={theme?.tinyFontSize} color={theme?.primary} bold
                                            />
                                        </TouchableOpacity>
                                    )}

                                    {/* Multiple action buttons */}
                                    {showButtonsInToolbar && buttons!.map((btn, index) => (
                                        <TouchableOpacity
                                            key={index}
                                            style={[styles?.addButton, btn?.variant === "outline" ? {
                                                borderWidth: 1,
                                                borderColor: theme.primary,
                                            } : { backgroundColor: theme?.primary }, btn?.disabled && { backgroundColor: "hsl(208, 8%, 90%)", borderColor: "hsl(208, 8%, 90%)" }, btn?.buttonContainerStyle]}
                                            onPress={btn.onPress}
                                            accessibilityLabel={btn.title}
                                            disabled={btn?.disabled}
                                        >
                                            <ZenText content={btn?.title} bold size={theme?.smallFontSize} color={btn?.disabled ? theme?.grey : btn?.variant === "outline" ? theme?.primary : theme?.light} style={btn?.textStyle} />
                                        </TouchableOpacity>
                                    ))}
                                </View>
                            )}
                        </View>
                    );
                }}
            </ThemeConsumer>


            {/* ── Location picker modal (self-contained) ─── */}
            {location && (
                <LocationPickerModal
                    visible={locationModalOpen}
                    title={location.title}
                    locationList={location.locationList}
                    onSelect={(item) => {
                        location.onSelect(item);
                        setLocationModalOpen(false);
                    }}
                    onClose={() => setLocationModalOpen(false)}
                />
            )}
            <CommonSortModal
                visible={isVisibleSort}
                options={sort?.options!}
                onSelect={(item) => { sort?.onSelect!(item); setIsVisibleSort(false) }}
                onClose={() => setIsVisibleSort(false)}
                title={sort?.title}
                selectedId={sort?.selectedId}
            />
        </>
    );
};

export default ListToolbar;